import React, { useRef } from "react";
import { Link } from "react-router-dom";
import {
    Handshake, Sparkles, TrendingUp, Gift, Users, ShieldCheck,
    ArrowRight, Mail, IndianRupee, Zap, Heart
} from "lucide-react";
import FemcareLogo from "../components/FemcareLogo";
import { useReveal, useSpotlight } from "../lib/motion";

const AFFILIATE_EMAIL = "affiliate@femcare.life";

const BENEFITS = [
    {
        icon: IndianRupee,
        title: "Up to 30% commission",
        desc: "Earn on every Premium subscription your audience buys through your unique link — recurring for the first 12 months.",
        span: "md:col-span-2",
    },
    {
        icon: Zap,
        title: "Fast payouts",
        desc: "Weekly payouts via UPI, bank transfer or PayPal once you cross ₹1,000.",
    },
    {
        icon: Gift,
        title: "Free Premium",
        desc: "You get Femcare Premium free for as long as you're an active partner.",
    },
    {
        icon: TrendingUp,
        title: "Real-time dashboard",
        desc: "Track clicks, sign-ups, conversions, and earnings — no spreadsheets, no waiting.",
    },
    {
        icon: Heart,
        title: "Creative kit",
        desc: "Editorial imagery, videos, Reels, blog copy — ready-to-post assets in English and Hindi.",
    },
    {
        icon: ShieldCheck,
        title: "First-party cookies",
        desc: "45-day attribution window with cross-device tracking. You get credit even for slow deciders.",
    },
];

const STEPS = [
    { n: "01", t: "Apply", d: "Send a note to affiliate@femcare.life with your audience & channel." },
    { n: "02", t: "Get approved", d: "We reply within 48 hours with your dashboard access." },
    { n: "03", t: "Share your link", d: "Post, review, embed — wherever your audience is." },
    { n: "04", t: "Get paid", d: "Weekly payouts, transparent dashboard, human support." },
];

const IDEAL_FOR = [
    "Wellness & lifestyle creators",
    "Women's health writers",
    "Parenting community leaders",
    "Editorial writers & bloggers",
    "Fitness & yoga coaches",
    "Podcast hosts",
];

export default function Affiliate() {
    const heroRef = useRef(null);
    useSpotlight(heroRef);
    useReveal();

    return (
        <div data-testid="affiliate-page" className="bg-femcare-cream">
            {/* HERO */}
            <section ref={heroRef} className="relative overflow-hidden spotlight">
                <div className="aurora grain" aria-hidden="true" />
                <div className="max-w-6xl mx-auto px-6 lg:px-12 pt-24 pb-28 relative">
                    <div className="flex items-center gap-4 mb-10 reveal">
                        <div className="f-coin">
                            <FemcareLogo className="w-9 h-9" />
                        </div>
                        <span className="text-xs uppercase tracking-[0.35em] text-femcare-accent font-medium">
                            Partner Program
                        </span>
                    </div>
                    <h1 className="editorial-headline text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-femcare-purple mb-8 reveal reveal-delay-1">
                        Grow with<br />
                        <span className="italic font-light">women who</span>{" "}
                        <span className="pink-gradient-text">grew with us.</span>
                    </h1>
                    <p className="text-lg md:text-xl text-femcare-purple/70 max-w-2xl leading-relaxed mb-10 reveal reveal-delay-2">
                        Join creators, clinicians and community leaders earning meaningful income by
                        recommending India&apos;s most-loved women&apos;s health app.
                    </p>
                    <div className="flex flex-wrap gap-4 reveal reveal-delay-3">
                        <a
                            href={`mailto:${AFFILIATE_EMAIL}?subject=Femcare%20affiliate%20application`}
                            className="btn-primary magnet"
                            data-testid="affiliate-apply-primary"
                        >
                            <Handshake className="w-4 h-4" />
                            Apply to join
                        </a>
                        <a
                            href="#how"
                            className="btn-secondary magnet"
                            data-testid="affiliate-how-link"
                        >
                            How it works
                        </a>
                    </div>

                    {/* Floating email chip */}
                    <div className="mt-14 inline-flex glass rounded-full pl-2 pr-5 py-2 items-center gap-3 reveal reveal-delay-4">
                        <span className="w-8 h-8 rounded-full bg-femcare-pink flex items-center justify-center">
                            <Mail className="w-4 h-4 text-white" />
                        </span>
                        <a
                            href={`mailto:${AFFILIATE_EMAIL}`}
                            className="font-medium text-femcare-purple hover:text-femcare-pink"
                            data-testid="affiliate-hero-email"
                        >
                            {AFFILIATE_EMAIL}
                        </a>
                    </div>
                </div>
            </section>

            {/* MARQUEE RIBBON */}
            <section className="bg-femcare-purple text-femcare-cream py-6 border-y border-pink-200/10 relative z-10">
                <div className="marquee">
                    <div className="marquee-track font-serif text-3xl md:text-4xl italic">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <React.Fragment key={i}>
                                <span>Partner with Femcare</span>
                                <span className="text-femcare-pink">·</span>
                                <span>{AFFILIATE_EMAIL}</span>
                                <span className="text-femcare-pink">·</span>
                                <span>Earn up to 30%</span>
                                <span className="text-femcare-pink">·</span>
                                <span>Weekly payouts</span>
                                <span className="text-femcare-pink">·</span>
                            </React.Fragment>
                        ))}
                    </div>
                    <div className="marquee-track font-serif text-3xl md:text-4xl italic" aria-hidden="true">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <React.Fragment key={i}>
                                <span>Partner with Femcare</span>
                                <span className="text-femcare-pink">·</span>
                                <span>{AFFILIATE_EMAIL}</span>
                                <span className="text-femcare-pink">·</span>
                                <span>Earn up to 30%</span>
                                <span className="text-femcare-pink">·</span>
                                <span>Weekly payouts</span>
                                <span className="text-femcare-pink">·</span>
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </section>

            {/* BENEFITS BENTO */}
            <section className="max-w-6xl mx-auto px-6 lg:px-12 py-24">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14">
                    <div className="md:col-span-5 reveal">
                        <p className="text-xs uppercase tracking-[0.3em] text-femcare-accent mb-4">
                            Why partner
                        </p>
                        <h2 className="editorial-headline text-4xl md:text-5xl lg:text-6xl text-femcare-purple">
                            Beautiful product.<br />
                            <span className="italic font-light">Beautiful economics.</span>
                        </h2>
                    </div>
                    <div className="md:col-span-6 md:col-start-7 flex items-end reveal reveal-delay-1">
                        <p className="text-lg text-femcare-purple/70 leading-relaxed">
                            Femcare&apos;s conversion rates lead the category — because the product does. When
                            you send someone here, they usually stay, subscribe, and thank you.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {BENEFITS.map((b, i) => (
                        <div
                            key={b.title}
                            className={`bento-tile p-7 reveal reveal-delay-${(i % 4) + 1} ${b.span || ""}`}
                            data-testid={`affiliate-benefit-${i}`}
                        >
                            <span className="w-11 h-11 rounded-full bg-femcare-petal flex items-center justify-center mb-5">
                                <b.icon className="w-5 h-5 text-femcare-pink" />
                            </span>
                            <h3 className="font-serif text-2xl text-femcare-purple mb-2 leading-tight">
                                {b.title}
                            </h3>
                            <p className="text-femcare-purple/70 text-sm leading-relaxed">{b.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="how" className="bg-white border-y border-femcare-border">
                <div className="max-w-6xl mx-auto px-6 lg:px-12 py-24">
                    <div className="mb-14 reveal">
                        <p className="text-xs uppercase tracking-[0.3em] text-femcare-accent mb-4">
                            How it works
                        </p>
                        <h2 className="editorial-headline text-4xl md:text-5xl text-femcare-purple">
                            Four soft steps<br />
                            <span className="italic font-light">from hello to payout.</span>
                        </h2>
                    </div>
                    <ol className="grid grid-cols-1 md:grid-cols-4 gap-5">
                        {STEPS.map((s, i) => (
                            <li
                                key={s.n}
                                className={`card-editorial p-7 reveal reveal-delay-${i + 1}`}
                                data-testid={`affiliate-step-${s.n}`}
                            >
                                <p className="font-serif text-5xl text-femcare-pink/80 mb-4">{s.n}</p>
                                <h3 className="font-serif text-xl text-femcare-purple mb-2">{s.t}</h3>
                                <p className="text-sm text-femcare-purple/70 leading-relaxed">{s.d}</p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* IDEAL FOR */}
            <section className="max-w-6xl mx-auto px-6 lg:px-12 py-24">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-5 reveal">
                        <p className="text-xs uppercase tracking-[0.3em] text-femcare-accent mb-4">
                            Made for
                        </p>
                        <h2 className="editorial-headline text-4xl md:text-5xl text-femcare-purple">
                            <span className="italic font-light">Voices women</span><br />
                            already trust.
                        </h2>
                    </div>
                    <div className="md:col-span-6 md:col-start-7 reveal reveal-delay-1">
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {IDEAL_FOR.map((t) => (
                                <li
                                    key={t}
                                    className="flex items-center gap-3 bg-white border border-femcare-border rounded-full px-5 py-3 text-sm text-femcare-purple font-medium"
                                >
                                    <span className="w-2 h-2 rounded-full bg-femcare-pink" />
                                    {t}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="max-w-6xl mx-auto px-6 lg:px-12 pb-24">
                <div className="relative overflow-hidden rounded-3xl">
                    <div className="aurora aurora-dark grain" aria-hidden="true" />
                    <div className="relative z-10 p-10 md:p-16 text-center text-white">
                        <div className="inline-flex items-center gap-2 glass-dark rounded-full px-4 py-2 text-xs uppercase tracking-[0.3em] mb-8 reveal">
                            <Sparkles className="w-3.5 h-3.5 text-femcare-pink" />
                            Now onboarding partners
                        </div>
                        <h2 className="editorial-headline text-4xl md:text-5xl lg:text-6xl mb-6 max-w-3xl mx-auto reveal reveal-delay-1">
                            Let&apos;s build<br />
                            <span className="italic font-light text-femcare-pink">something soft.</span>
                        </h2>
                        <p className="text-lg text-pink-100/80 max-w-xl mx-auto mb-10 reveal reveal-delay-2">
                            Send a quick note about you and your audience — we usually reply the same day.
                        </p>
                        <a
                            href={`mailto:${AFFILIATE_EMAIL}?subject=Femcare%20affiliate%20application&body=Hi%20Femcare%20team%2C%0A%0AI%27d%20like%20to%20apply%20for%20the%20affiliate%20program.%0A%0A-%20Channel%3A%20%0A-%20Audience%20size%3A%20%0A-%20Main%20topic%3A%20%0A%0AThanks%21`}
                            className="inline-flex items-center gap-2 bg-white text-femcare-purple rounded-full px-8 py-4 font-medium magnet reveal reveal-delay-3"
                            data-testid="affiliate-cta-email"
                        >
                            <Mail className="w-4 h-4" />
                            Email {AFFILIATE_EMAIL}
                            <ArrowRight className="w-4 h-4" />
                        </a>

                        <p className="mt-10 text-xs text-pink-100/60 tracking-[0.25em] uppercase reveal reveal-delay-4">
                            <Users className="w-3.5 h-3.5 inline mr-1" /> Growing every day
                        </p>
                    </div>
                </div>
            </section>

            {/* SECONDARY LINKS */}
            <section className="pb-16 text-center">
                <p className="text-sm text-femcare-purple/60">
                    Questions? Peek at our{" "}
                    <Link to="/support" className="text-femcare-pink underline decoration-femcare-pink/40 hover:decoration-femcare-pink underline-offset-4">
                        Help Center
                    </Link>{" "}
                    or read the{" "}
                    <Link to="/privacy" className="text-femcare-pink underline decoration-femcare-pink/40 hover:decoration-femcare-pink underline-offset-4">
                        Privacy Policy
                    </Link>
                    .
                </p>
            </section>
        </div>
    );
}
